<template>
  <div>
    <validation-observer ref="form" novalidate>
      <div class="mb-3">
        <div class="data-name">
          ¿Se realizará Pareo Ocupacional?
        </div>
        <div>
          <validation-provider
            ref="hasOccupationalPairing"
            v-slot="{ errors }"
            rules="required"
          >
            <custom-radio
              v-model="model.hasOccupationalPairing"
              class="mt-1 ml-n3 custom-radio"
              custom-error-msg="Favor contestar Sí o No."
              :error="errors[0]"
              :is-disabled="isReadOnly"
              is-inline
              name="hasOccupationalPairing"
              :options="yesNoOptions"
            />
          </validation-provider>
        </div>
      </div>
      <div v-if="model.hasOccupationalPairing">
        <section-title
          container-class="tag-badge-main-info mt-4 mb-4"
          content="VI. Descripción de la Meta según la fuente de información ocupacional"
          icon="fas fa-clipboard-list-check"
        />
        <div class="my-3">
          <section-title
            content="Descripción de la Meta según la fuente de información ocupacional:"
            has-line
          />
          <goals v-model="model.goals" :is-read-only="isReadOnly" />
        </div>
        <div class="my-3">
          <section-title
            content="Descripción de la profesión u oficio:"
            has-line
          />
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-text-area
              v-model="model.professionDescription"
              :count-classes="['d-block', 'small-counter', 'text-right']"
              custom-error-msg="El campo es requerido."
              :disabled="isReadOnly"
              :error="errors[0]"
              :maxlength="5000"
              :show-char-count="!isReadOnly"
            />
          </validation-provider>
        </div>
        <div class="my-3">
          <section-title content="Adiestramiento Requerido:" has-line />
          <div class="data-name">
            Según la O*NET, esta meta vocacional requiere de algun título
            universitario o algo de universidad tales como:
          </div>
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-text-area
              v-model="model.trainingRequired"
              :count-classes="['d-block', 'small-counter', 'text-right']"
              custom-error-msg="El campo es requerido."
              :disabled="isReadOnly"
              :error="errors[0]"
              :maxlength="5000"
              :show-char-count="!isReadOnly"
            />
          </validation-provider>
        </div>
        <div class="my-3">
          <section-title
            content="Licencias o Certificaciones requeridas:"
            has-line
          />
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-text-area
              v-model="model.licensesRequired"
              :count-classes="['d-block', 'small-counter', 'text-right']"
              custom-error-msg="El campo es requerido."
              :disabled="isReadOnly"
              :error="errors[0]"
              :maxlength="5000"
              :show-char-count="!isReadOnly"
            />
          </validation-provider>
        </div>
        <div class="my-3">
          <section-title content="Metas alternas en la O*Net:" has-line />
          <goals
            v-model="model.alternativeGoals"
            aggregable
            :is-read-only="isReadOnly"
            @add="addAlternativeGoal"
            @remove="removeAlternativeGoal($event)"
          />
        </div>
      </div>
    </validation-observer>
  </div>
</template>

<script>
//mixins
import alertMixin from '@/mixins/alert.mixin.js';

//utils
import { copy } from '@/utils/objUtils';
import RADIO_OPTIONS from '@/utils/constants/customRadioOptions';

//filters

//components
import CustomTextArea from 'CustomTextArea';
import SectionTitle from '@/shared/components/SectionTitle.vue';
import CustomRadio from '@/components/common/CustomRadio';

//local-components
import Goals from './components/Goals.vue';

//services
import { getSix } from '@/modules/vocational-evaluation/api.js';

//constants
const ONET_CODE_ID = 3;

export default {
  name: 'VocationalEducationPartSix',
  components: {
    CustomTextArea,
    SectionTitle,
    CustomRadio,
    Goals,
  },
  mixins: [alertMixin],
  props: {
    id: {
      type: [Number, String],
      default: () => null,
    },
    appointmentId: {
      type: [Number, String],
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    value: {
      type: Object,
      required: true,
    },
    initialModel: {
      type: Object,
      required: false,
      default: () => null,
    },
    keepUpdates: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    isReadOnly: {
      type: Boolean,
      required: true,
      default: () => false,
    },
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    filled() {
      return this.initialModel?.filled ?? false;
    },
    yesNoOptions() {
      return RADIO_OPTIONS;
    },
  },
  watch: {
    'model.hasOccupationalPairing'(value) {
      this.$emit('update:has-occupational-pairing', value);
    },
  },
  beforeDestroy() {
    this.$emit('update:initialModel', this.model);
  },
  async mounted() {
    this.$emit('update:disabled', true);
    await this.get();
    this.$emit('update:disabled', false);
  },
  methods: {
    setModelFrom(value) {
      const data = copy(value);
      this.model = data;
      this.$emit('update:initialModel', data);
    },
    async get() {
      if (this.keepUpdates && this.filled) {
        this.setModelFrom(this.initialModel);
        return;
      }

      if (!this.appointmentId) return;

      const { data } = await getSix({
        id: this.id,
        appointmentId: this.appointmentId,
      });
      if (!data.alternativeGoals.length) {
        const { goal } = data.goals.find((x) => x.goal.id == ONET_CODE_ID);
        data.alternativeGoals.push({
          goal,
          number: null,
          profession: null,
          isAlternative: true,
        });
      }
      this.setModelFrom({ ...data, filled: true });
    },
    async validate() {
      return await this.$refs.form.validate();
    },
    addAlternativeGoal() {
      const { goal } = this.model.goals.find((x) => x.goal.id == ONET_CODE_ID);
      this.model.alternativeGoals.push({
        goal,
        number: null,
        profession: null,
        isAlternative: true,
      });
    },
    removeAlternativeGoal(key) {
      this.model.alternativeGoals.splice(key, 1);
    },
  },
};
</script>
<style scoped>
::v-deep .custom-radio {
  margin-bottom: 20px;
}
::v-deep .custom-radio .custom-control-label {
  font-weight: 700;
}
::v-deep
  .custom-control-input:disabled:checked
  ~ .custom-control-label::before {
  background-color: #3f5b60 !important;
  border-color: #3f5b60 !important;
}
</style>
